import { LD_FLAGS } from "@src/config/launchDarkly";
import { autoPoActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getNavSections, INavLeaf, INavSection, ROUTE_LABELS } from "./navItems";
import { NavMenu } from "./NavMenu";
import styles from "./TopNav.module.scss";
import { UserMenu } from "./UserMenu";
import { PricingIcon } from "@src/assets/sidebar-Icons";

const sectionContainsPath = (section: INavSection, pathname: string): boolean =>
	section.items.some((item) => {
		if (item.to?.split("?")[0] === pathname) return true;
		return !!item.children?.some((child) => child.to?.split("?")[0] === pathname);
	});

export const TopNav: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const ldClient = useLDClient();
	const enableSalesPage = ldClient?.variation(LD_FLAGS.enableSalesPage, false) ?? false;
	const enableManualPoCreation = ldClient?.variation(LD_FLAGS.enableManualPoCreation, false) ?? false;

	const hasConsignor = useAppSelector((state) => state.settings.hasConsignor);
	const hasSlashUser = useAppSelector((state) => state.settings.hasSlashUser);
	const hasEventVue = useAppSelector((state) => state.settings.hasEventVue);
	const hasDataVue = useAppSelector((state) => state.settings.hasDataVue);
	const hasStandardReport = useAppSelector((state) => state.settings.hasStandardReport);
	const missingInformationCount = useAppSelector((state) => state.autoPo.missingInformationCount.data);

	useEffect(() => {
		if (!hasConsignor) return;

		const fetchCount = () => dispatch(autoPoActions.getMissingInformationCount());
		fetchCount();

		window.addEventListener("focus", fetchCount);
		return () => window.removeEventListener("focus", fetchCount);
	}, [dispatch, hasConsignor]);

	const sections = useMemo(
		() =>
			getNavSections(
				{ hasConsignor, hasSlashUser, hasEventVue, hasDataVue, hasStandardReport },
				{ enableSalesPage, enableManualPoCreation, missingInformationCount: missingInformationCount ?? 0 },
			),
		[
			hasConsignor,
			hasSlashUser,
			hasEventVue,
			hasDataVue,
			hasStandardReport,
			enableSalesPage,
			enableManualPoCreation,
			missingInformationCount,
		],
	);

	const activeLabel = ROUTE_LABELS[pathname] ?? ROUTE_LABELS[`/${pathname.split("/")[1]}`] ?? "Inventory";

	const openedNewTabsRef = useRef<Record<string, Window | null>>({});

	const handleSelect = (item: INavLeaf) => {
		if (item.href) {
			window.open(item.href, "_blank");
			return;
		}

		if (!item.to) return;

		if (item.openInNewTab) {
			if (pathname === item.to) {
				window.location.reload();
				return;
			}

			const existingTab = openedNewTabsRef.current[item.to];
			if (existingTab && !existingTab.closed) {
				try {
					existingTab.focus();
					existingTab.location.href = item.to;
					existingTab.location.reload();
					return;
				} catch {
					// If the tab reference is no longer usable, fall back to opening a new one.
				}
			}

			openedNewTabsRef.current[item.to] = window.open(item.to, "_blank");
			return;
		}

		navigate(item.to);
	};

	return (
		<header className={styles.root}>
			<div className={styles.currentRoute}>
				<PricingIcon isActive={false} />
				<span>{activeLabel}</span>
			</div>

			<nav className={styles.sections}>
				{sections.map((section) => (
					<NavMenu
						key={section.id}
						label={section.label}
						items={section.items}
						isActive={sectionContainsPath(section, pathname)}
						onSelect={handleSelect}
					/>
				))}
			</nav>

			<div className={styles.right}>
				<UserMenu />
			</div>
		</header>
	);
};
